/**
 * Super-admin verification helper used by the runtime dashboard's
 * SuperAdminGate component. Lives in the runtime `src/lib` (not in
 * `dashboard/src/lib`) so the existing Node-environment vitest setup
 * can import it without breaking the dashboard's `rootDir` boundary.
 *
 * Defence-in-depth UI gate. The runtime now uses a dedicated Clerk
 * instance (primary domain tmagent.io) with invite-only sign-up — no
 * customer can mint a session against the runtime origin. The product
 * app uses a separate Clerk instance, so there is no shared cookie to
 * carry a non-admin session over. This helper still hits
 * /api/v1/auth/whoami — which sits behind the existing
 * requireSuperAdmin middleware — so a misconfigured Clerk dashboard or
 * an admin who later loses super-admin status cannot see the staff UI
 * shell. The API response is the source of truth.
 *
 * SUPER_ADMIN_USER_ID is NOT exposed to the frontend; the API response
 * code is the source of truth.
 *
 * Pure logic — no React, no DOM. The React component
 * (dashboard/src/components/SuperAdminGate.tsx) wires this up to Clerk
 * + window.location.
 */

export const APP_URL = 'https://app.tmagents.com';

export type AdminCheckResult = 'authorized' | 'rejected' | 'error';

export interface AdminCheckDeps {
  /** Base URL for the runtime API (matches VITE_API_URL). */
  apiBase: string;
  /** Returns the current Clerk session JWT, or null if unavailable. */
  getToken: () => Promise<string | null>;
  /** Clerk's signOut — invoked on a non-admin session before redirect. */
  signOut: () => Promise<unknown>;
  /** Hard navigation away from the runtime app (defaults to window.location). */
  redirect: (url: string) => void;
  /** Override for tests; defaults to globalThis.fetch. */
  fetch?: typeof fetch;
}

export async function verifySuperAdmin(
  deps: AdminCheckDeps,
): Promise<AdminCheckResult> {
  const fetcher = deps.fetch ?? globalThis.fetch;

  let res: Response;
  try {
    const token = await deps.getToken();
    const headers: Record<string, string> = {};
    if (token) headers['Authorization'] = `Bearer ${token}`;
    res = await fetcher(`${deps.apiBase}/api/v1/auth/whoami`, { headers });
  } catch {
    return 'error';
  }

  if (res.ok) return 'authorized';

  if (res.status === 401 || res.status === 403) {
    // Sign out FIRST so the shared Clerk cookie is cleared, then bounce
    // to the customer app. If signOut throws we still redirect — the
    // important thing is to get the user off the runtime origin without
    // any chance of admin UI rendering.
    try {
      await deps.signOut();
    } catch {
      // intentionally swallowed — we still redirect on signOut failure
    }
    deps.redirect(APP_URL);
    return 'rejected';
  }

  return 'error';
}
